import { subDays, subMonths, subWeeks } from 'date-fns';

import type { TotalFilters } from './wallet-dashboard.wire';

// Período do Dashboard de carteira como o legado monta. Puro: a tela guarda o estado e o agente usa as mesmas funções.

/** Os botões do período, na ordem do legado (DashboardOverview.tsx:41: HOJE, ONTEM, 1SEM, 1M, 3M, 6M). */
export const PERIOD_PRESETS = ['today', 'yesterday', '1w', '1m', '3m', '6m'] as const;
export type PeriodPreset = (typeof PERIOD_PRESETS)[number];

/** `dateFormatedNonHours(date.toISOString())` (DateFormat.ts:150-153): o dia **em UTC**, como o legado manda. */
const utcDay = (date: Date) => date.toISOString().slice(0, 10);

/**
 * `applyFilterByPeriod(index)` (DateFormat.ts:182-211): o início é hoje às 00:00 (HOJE) ou agora menos 1 dia,
 * 1 semana, 1, 3 ou 6 meses; o fim é **agora**. Os dois saem como `yyyy-MM-dd` do `toISOString()`.
 */
export function presetRange(preset: PeriodPreset, now: Date = new Date()): Pick<TotalFilters, 'initialDate' | 'finalDate'> {
  const start = (() => {
    switch (preset) {
      case 'today': {
        const midnight = new Date(now);
        midnight.setHours(0, 0, 0, 0);
        return midnight;
      }
      case 'yesterday':
        return subDays(now, 1);
      case '1w':
        return subWeeks(now, 1);
      case '1m':
        return subMonths(now, 1);
      case '3m':
        return subMonths(now, 3);
      case '6m':
        return subMonths(now, 6);
    }
  })();
  return { initialDate: utcDay(start), finalDate: utcDay(now) };
}

const civil = /^(\d{4})-(\d{2})-(\d{2})$/;

function localMidnight(value: string): Date {
  const match = civil.exec(value);
  if (!match) throw new Error(`invalid civil date: ${value}`);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/**
 * "Definir período" (DashboardOverview.tsx:57-66): o calendário devolve meia-noite local em ISO
 * (Calendar.tsx:84-86) e a tela corta no `T`. Mesma conta aqui, a partir das datas civis do popup.
 */
export function customRange(start: string, end: string): Pick<TotalFilters, 'initialDate' | 'finalDate'> {
  return { initialDate: utcDay(localMidnight(start)), finalDate: utcDay(localMidnight(end)) };
}

/** Primeira consulta (DashboardContainer.tsx:24-30): HOJE, todas as carteiras. */
export function initialTotalFilters(now: Date = new Date()): TotalFilters {
  return presetRange('today', now);
}

/** Período novo sobre os filtros atuais: as carteiras ficam (DashboardOverview.tsx:49-53). */
export function withPeriod(filters: TotalFilters, range: Pick<TotalFilters, 'initialDate' | 'finalDate'>): TotalFilters {
  return { ...filters, initialDate: range.initialDate, finalDate: range.finalDate };
}

/** Carteiras novas sobre os filtros atuais (SelectedWalletContainer.tsx:22-40): o período fica. */
export function withWallets(filters: TotalFilters, walletsCode: readonly string[]): TotalFilters {
  return { ...filters, walletsCode: [...walletsCode] };
}
